import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Loader2, Plus, Search } from 'lucide-react';
import { CompletionsList } from '@/features/completions/CompletionsList';
import { CompletionForm } from '@/features/completions/CompletionForm';
import { useToast } from '@/hooks/use-toast';

interface CompletionRecord {
  id: number;
  customerId: number | null;
  installationId: number | null;
  customerName: string;
  customerAddress: string;
  customerEmail: string | null;
  customerPhone: string | null;
  projectRefNumber: string;
  installationAddress: string;
  installationDescription: string;
  status: string;
  signedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export default function HiesCompletionPage() {
  const [selectedRecord, setSelectedRecord] = useState<CompletionRecord | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { toast } = useToast();

  // Fetch completion records for installer (matching Site Survey fetch pattern)
  const {
    data: records,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['completions', 'installer', 'v4'], // v4 to force cache refresh after fix
    queryFn: async () => {
      const response = await fetch(`/api/completion-of-works/installer?_t=${Date.now()}`, {
        credentials: 'include',
        // Remove Authorization header - use cookie-based auth like Site Survey
      });

      if (!response.ok) {
        if (response.status === 404) {
          return [];
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return (data || []) as CompletionRecord[];
    },
    staleTime: 0, // Always fetch fresh data (matching Site Survey)
    gcTime: 0, // Don't cache (matching Site Survey)
  });

  // Fetch Won customers that can have completions created
  // The backend supports both installation IDs and customer IDs
  const { data: allWonCustomers, isLoading: isLoadingCustomers } = useQuery({
    queryKey: ['/api/customers', 'won', 'v16'],
    queryFn: async () => {
      // Fetch Won customers (pipelineStageId=8) and filter out those with existing HIES records on the frontend
      const response = await fetch(`/api/customers?pipelineStageId=8&_t=${Date.now()}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 404) {
          return [];
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Handle error response
      if (data.error) {
        return [];
      }
      return data || [];
    },
    staleTime: 0, // Always fetch fresh data (matching Site Survey)
    gcTime: 0, // Don't cache (matching Site Survey)
    enabled: !isLoading, // Only fetch after records are loaded
  });

  // Filter out customers who already have HIES completion records
  const wonCustomers = useMemo(() => {
    if (!allWonCustomers || !records) return allWonCustomers || [];

    // Get list of customer IDs that already have completion records
    const customersWithRecords = new Set(records.map((r) => r.customerId));

    // Filter out customers who already have records
    const eligibleCustomers = allWonCustomers.filter(
      (customer: any) => !customersWithRecords.has(customer.id)
    );

    return eligibleCustomers;
  }, [allWonCustomers, records]);

  const handleCreateNew = async (customerId: number) => {
    setIsCreating(true);
    try {
      // The backend endpoint supports both installation IDs and customer IDs
      // When it receives a customer ID, it will find the installation or create without one
      const response = await fetch(`/api/completion-of-works/from-installation/${customerId}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          // Remove Authorization header - use cookie-based auth
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        toast({
          title: 'Success',
          description: 'HIES Completion form created successfully',
        });
        setSelectedRecord(data.record);
        refetch();
      } else {
        throw new Error(data.error || 'Failed to create completion form');
      }
    } catch (error) {
      console.error('Error creating completion form:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create completion form',
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleRecordSelect = (record: CompletionRecord) => {
    setSelectedRecord(record);
  };

  const handleBack = () => {
    setSelectedRecord(null);
    refetch();
  };

  // Filter customers based on search term
  const filteredCustomers = useMemo(() => {
    if (!wonCustomers || !searchTerm) {
      return wonCustomers || [];
    }

    const lowerSearch = searchTerm.toLowerCase();
    return wonCustomers.filter((customer: any) => {
      // Handle different field naming conventions from backend
      const customerName = (
        customer.name ||
        customer.customerName ||
        (customer.firstName && customer.lastName
          ? `${customer.firstName} ${customer.lastName}`
          : '') ||
        ''
      ).toLowerCase();

      const address = (customer.address || customer.customerAddress || '').toLowerCase();

      const status = (customer.status || '').toLowerCase();

      return (
        customerName.includes(lowerSearch) ||
        address.includes(lowerSearch) ||
        status.includes(lowerSearch)
      );
    });
  }, [wonCustomers, searchTerm]);

  // Show loading if either query is still loading
  if (isLoading || isLoadingCustomers) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading HIES Completion...</span>
      </div>
    );
  }

  // If a specific record is selected or we're in form mode
  if (selectedRecord) {
    return (
      <CompletionForm
        record={selectedRecord}
        onBack={handleBack}
        onComplete={() => {
          handleBack();
          toast({
            title: 'Success',
            description: 'HIES Completion form saved successfully',
          });
        }}
      />
    );
  }

  // Main list view
  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">HIES Completion Forms</h1>
        <p className="text-gray-600 mt-2">Manage and complete HIES forms for your installations</p>
      </div>

      <Tabs defaultValue="pending" className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6 md:max-w-2xl md:mx-auto">
          <TabsTrigger value="pending">
            Pending (
            {records?.filter((r) => r.status === 'draft' || r.status === 'pending').length || 0})
          </TabsTrigger>
          <TabsTrigger value="signed">
            Signed ({records?.filter((r) => r.status === 'signed').length || 0})
          </TabsTrigger>
          <TabsTrigger value="all">All ({records?.length || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          <CompletionsList
            records={records?.filter((r) => r.status === 'draft' || r.status === 'pending') || []}
            onSelect={handleRecordSelect}
            emptyMessage="No pending HIES forms"
          />
        </TabsContent>

        <TabsContent value="signed">
          <CompletionsList
            records={records?.filter((r) => r.status === 'signed') || []}
            onSelect={handleRecordSelect}
            emptyMessage="No signed HIES forms"
          />
        </TabsContent>

        <TabsContent value="all">
          <CompletionsList
            records={records || []}
            onSelect={handleRecordSelect}
            emptyMessage="No HIES forms found"
          />
        </TabsContent>
      </Tabs>

      {/* Quick create section for customers without completions */}
      {wonCustomers && wonCustomers.length > 0 ? (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Create New HIES Form</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Select a customer to create a new HIES completion form:
            </p>

            {/* Search bar - Enhanced for tablets */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 md:h-5 md:w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by customer name or address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12 md:h-14 text-base md:text-lg"
              />
            </div>

            {/* Filtered results count */}
            {searchTerm && (
              <p className="text-sm text-gray-600 mb-3">
                Found {filteredCustomers.length} available customers
              </p>
            )}

            <div className="space-y-2 max-h-96 md:max-h-[500px] overflow-y-auto">
              {(() => {
                // filteredCustomers already excludes those with existing completion records
                const availableCustomers = filteredCustomers;

                if (availableCustomers.length === 0) {
                  return (
                    <div className="text-center py-8 text-gray-500">
                      {searchTerm ? (
                        <p>No customers found matching "{searchTerm}"</p>
                      ) : (
                        <p>All customers already have HIES forms created</p>
                      )}
                    </div>
                  );
                }

                return availableCustomers.map((customer: any) => {
                  // Handle different field naming conventions from backend
                  const customerName =
                    customer.name ||
                    customer.customerName ||
                    (customer.firstName && customer.lastName
                      ? `${customer.firstName} ${customer.lastName}`
                      : `Customer #${customer.id}`);

                  const address = customer.address || customer.customerAddress || 'No address';

                  const siteSurveyStatus = customer.site_survey_status || customer.siteSurveyStatus;

                  return (
                    <div
                      key={customer.id}
                      className="flex flex-col md:flex-row md:items-center md:justify-between p-3 md:p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div>
                        <p className="font-medium">{customerName}</p>
                        <p className="text-sm text-gray-600">{address}</p>
                        <div className="flex gap-2 mt-1">
                          {customer.status && (
                            <span className="text-xs text-gray-500">Status: {customer.status}</span>
                          )}
                          {siteSurveyStatus && (
                            <span className="text-xs text-green-600">
                              Site Survey: {siteSurveyStatus}
                            </span>
                          )}
                        </div>
                      </div>
                      <Button
                        onClick={() => handleCreateNew(customer.id)}
                        disabled={isCreating}
                        size="sm"
                        className="mt-3 md:mt-0 w-full md:w-auto"
                      >
                        {isCreating ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Plus className="h-4 w-4 mr-2" />
                        )}
                        Create HIES Form
                      </Button>
                    </div>
                  );
                });
              })()}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>No Eligible Installations</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600">
              No installations found with completed site surveys in the Won stage that don't already
              have HIES forms.
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Complete site surveys for Won customers to create HIES forms.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
